import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { format } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    Button, 
    CircularProgress, 
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem,
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { setTimeMode } from "state";
import { AIRLINE_COUNTRY_CODE } from 'state/constants';
import api from 'state/axios';
import { getFlagUrl, formatNumber } from 'state/helpers';
import FlexBetween from 'components/FlexBetween'; 
import FlexCenter from 'components/FlexCenter'; 
import VietnamFlag from "img/VietnamFlag.png";
import UnitedNationsFlag from "img/UnitedNationsFlag.png";


const InventoryByProductIdDialog = ({ 
    open, 
    onClose,
    selectedProductName,
    inventoryProducts
}) => {
    useAuthCheck();
    const dispatch = useDispatch();
    const timeMode = useSelector((state) => state.timemode);
    const params = useSelector((state) => state.params);
    const theme = useTheme();
    const primaryDark = theme.palette.primary.dark;
    const neutralDark = theme.palette.neutral.dark;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);    

    const [suppliers, setSuppliers] = useState([]);
    const [selectedSupplier, setSelectedSupplier] = useState('All');
    
    const AIRLINE_NATIONAL_CODE = params?.find((item) => item.param === "AIRLINE_COUNTRY_CODE")?.value || 'VN';    
    const CARRIER_NATIONAL_CODE = AIRLINE_NATIONAL_CODE || AIRLINE_COUNTRY_CODE;
    const flagUrl = timeMode === "UTC" 
        ? (getFlagUrl("un") || UnitedNationsFlag) 
        : (getFlagUrl(CARRIER_NATIONAL_CODE) || VietnamFlag);
  
    useEffect(() => {
        const fetchSuppliers = async () => {
            try {
                setError(null);
                setLoading(true);

                const url = '/suppliers/active';
                const res = await api.get(url);
                
                setSuppliers(res.data);
            } catch (error) {
                setError(error);
                toast.error(error?.message || "Failed to fetch suppliers!");
            } finally {
                setLoading(false);
            }
        };

        fetchSuppliers();                
    }, []);

    const handleSupplierChange = (event) => {
        setSelectedSupplier(event.target.value);
    };
    
    const handleCloseDialog = () => {
        onClose();
    };

    const filteredProducts = selectedSupplier === 'All'
        ? inventoryProducts
        : inventoryProducts.filter(product => 
            suppliers.find(supplier => supplier._id === selectedSupplier)?.supplierName === product.supplierName
        );


    // Calculate totals for quantity fields
    const totals = filteredProducts.reduce((acc, product) => {
        acc.stockIn += (product.preorderStockIn || 0) + (product.directStockIn || 0);
        acc.previousReceipt += (product.preorderPreviousReceipt || 0) + (product.directPreviousReceipt || 0);
        acc.nextRetrieval += (product.preorderNextRetrieval || 0) + (product.directNextRetrieval || 0);
        acc.stockOn += (product.preorderStockOn || 0) + (product.directStockOn || 0);
        acc.shrinkage += (product.preorderShrinkage || 0) + (product.directShrinkage || 0);
        acc.combo += (product.preorderCombo || 0) + (product.directCombo || 0);
        acc.loss += (product.preorderLoss || 0) + (product.directLoss || 0);
        acc.consume += (product.preorderConsume || 0) + (product.directConsume || 0);
        return acc;
    }, {
        stockIn: 0,
        previousReceipt: 0,
        nextRetrieval: 0,
        stockOn: 0,
        shrinkage: 0,
        combo: 0,
        loss: 0,
        consume: 0,
    });

    // Calculate totals for Preorder and Direct
    const preorderTotals = filteredProducts.reduce((acc, product) => {
        acc.stockIn += product.preorderStockIn || 0;
        acc.previousReceipt += product.preorderPreviousReceipt || 0;
        acc.nextRetrieval += product.preorderNextRetrieval || 0;
        acc.stockIn += product.preorderStockIn || 0;
        acc.stockOn += product.preorderStockOn || 0;
        acc.shrinkage += product.preorderShrinkage || 0;
        acc.combo += product.preorderCombo || 0;
        acc.loss += product.preorderLoss || 0;
        acc.consume += product.preorderConsume || 0;
        return acc;
    }, {
        stockIn: 0,
        previousReceipt: 0,
        nextRetrieval: 0,
        stockOn: 0,
        shrinkage: 0,
        combo: 0,
        loss: 0,
        consume: 0,
    });

    const directTotals = filteredProducts.reduce((acc, product) => {
        acc.stockIn += product.directStockIn || 0;
        acc.previousReceipt += product.directPreviousReceipt || 0;
        acc.nextRetrieval += product.directNextRetrieval || 0;
        acc.stockOn += product.directStockOn || 0;
        acc.shrinkage += product.directShrinkage || 0;
        acc.combo += product.directCombo || 0;
        acc.loss += product.directLoss || 0;
        acc.consume += product.directConsume || 0;
        return acc;
    }, {
        stockIn: 0,
        previousReceipt: 0,
        nextRetrieval: 0,
        stockOn: 0,
        shrinkage: 0,
        combo: 0,
        loss: 0,
        consume: 0,
    });

    return (
        <Dialog 
            open={open} 
            onClose={handleCloseDialog}
            sx={{ '& .MuiDialog-paper': { minWidth: { xs: '90%', sm: '95%' }} }}
        >
            <DialogTitle borderBottom={`1px solid ${neutralDark}`}>
                <FlexBetween paddingBottom="8px" borderBottom={`1px solid ${neutralDark}`}>
                    <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        color="primary"                                                
                    >
                        INVENTORY - {selectedProductName}
                    </Typography>
                    <Box 
                        display="flex" 
                        alignItems="center"
                        onClick={() => dispatch(setTimeMode())}
                    >
                        <img
                            alt="Flag"
                            width={25}
                            height={25}
                            src={flagUrl}
                            style={{ 
                                cursor: "pointer", 
                                objectFit: "cover", 
                                borderRadius: "50%",
                                transition: "opacity 0.3s",
                            }}
                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                        />
                    </Box>
                </FlexBetween>                
                
                <Box display="flex" flexDirection="column" pt={1} gap={2}>
                    <FlexBetween gap={1}>
                        <FormControl sx={{ width: '100%' }}>
                            <InputLabel>Supplier</InputLabel>
                            <Select label="Supplier" size='small' value={selectedSupplier} onChange={handleSupplierChange}>
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value=""><em>None</em></MenuItem>
                                {suppliers.map((supplier) => (
                                    <MenuItem key={supplier._id} value={supplier._id}>{supplier.supplierName}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>                        
                    </FlexBetween>                 
                </Box>
            </DialogTitle>
            <DialogContent>
                {loading ? (
                    <FlexCenter sx={{ height: '100%', width: '100%', mt: 1 }}>
                        <CircularProgress />
                    </FlexCenter>
                ) : filteredProducts && filteredProducts.length > 0 ? (
                    <Box>
                        {!isMobile ? (
                            <TableContainer>
                                <Table sx={{ minWidth: 650 }} size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', color: primaryDark }} rowSpan={2}>Date</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: primaryDark }} rowSpan={2}>Flight No</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: primaryDark }} rowSpan={2}>Crew Route</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', color: primaryDark }}>Type</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: primaryDark }} align="right">Stock In</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: primaryDark }} align="right">Previous Receipt</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: primaryDark }} align="right">Next Retrieval</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: primaryDark }} align="right">Stock On</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: primaryDark }} align="right">Shrinkage</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: primaryDark }} align="right">Combo</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: primaryDark }} align="right">Loss</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', color: primaryDark }} align="right">Consume</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredProducts.map((product) => (
                                            <>
                                                <TableRow key={`${product?._id}-preorder`}>
                                                    <TableCell rowSpan={2}>
                                                        {timeMode === "UTC"
                                                            ? format(new Date(product?.flightDate), 'dd/MM/yyyy')
                                                            : format(new Date(product?.flightDateLocalBase), 'dd/MM/yyyy')}
                                                    </TableCell>
                                                    <TableCell rowSpan={2}>{product?.flightNo}</TableCell>
                                                    <TableCell rowSpan={2}>{product?.crewRoute}</TableCell>
                                                    <TableCell>Preorder</TableCell>
                                                    <TableCell align="right" sx={{ color: product.preorderStockIn !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.preorderStockIn)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: product.preorderPreviousReceipt !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.preorderPreviousReceipt)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: product.preorderNextRetrieval !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.preorderNextRetrieval)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: product.preorderStockOn !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.preorderStockOn)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: product.preorderShrinkage !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.preorderShrinkage)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: product.preorderCombo !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.preorderCombo)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: product.preorderLoss !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.preorderLoss)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: product.preorderConsume !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.preorderConsume)}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow key={`${product?._id}-direct`}>
                                                    <TableCell>Direct</TableCell>
                                                    <TableCell align="right" sx={{ color: product.directStockIn !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.directStockIn)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: product.directPreviousReceipt !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.directPreviousReceipt)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: product.directNextRetrieval !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.directNextRetrieval)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: product.directStockOn !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.directStockOn)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: product.directShrinkage !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.directShrinkage)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: product.directCombo !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.directCombo)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: product.directLoss !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.directLoss)}
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: product.directConsume !== 0 ? primaryDark : 'inherit' }}>
                                                        {formatNumber(product?.directConsume)}
                                                    </TableCell>
                                                </TableRow>
                                            </>
                                        ))}
                                        <TableRow>
                                            <TableCell colSpan={4} sx={{ textAlign: 'center', fontWeight: 'bold', color: primaryDark }}>Total</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', color: primaryDark }}>{formatNumber(totals?.stockIn)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', color: primaryDark }}>{formatNumber(totals?.previousReceipt)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', color: primaryDark }}>{formatNumber(totals?.nextRetrieval)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', color: primaryDark }}>{formatNumber(totals?.stockOn)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', color: primaryDark }}>{formatNumber(totals?.shrinkage)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', color: primaryDark }}>{formatNumber(totals?.combo)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', color: primaryDark }}>{formatNumber(totals?.loss)}</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', color: primaryDark }}>{formatNumber(totals?.consume)}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box>
                                {filteredProducts.map((product) => (                            
                                    <Box 
                                        key={product._id}
                                        display="flex" 
                                        flexDirection={isMobile ? 'column' : 'column'} 
                                        justifyContent="space-between" 
                                        gap={1} 
                                        py={1}
                                        borderBottom={`1px solid ${neutralDark}`}
                                    >
                                        <Box display="flex" justifyContent={"flex-start"} gap={1}>
                                            <Typography color={primaryDark}>
                                                {timeMode === "UTC"
                                                    ? format(new Date(product.flightDate), 'dd/MM/yyyy')
                                                    : format(new Date(product.flightDateLocalBase), 'dd/MM/yyyy')}
                                            </Typography> 
                                            <Typography color={primaryDark}>
                                                {product.flightNo}
                                            </Typography> 
                                            <Typography color={primaryDark}>
                                                {product.crewRoute}
                                            </Typography> 
                                        </Box>
                                        <Box display="flex" flexDirection={isMobile ? 'row' : 'column'} justifyContent="space-between" alignItems="flex-start" gap={2}>                                
                                            {['Preorder', 'Direct'].map((category) => {
                                                const isPreorder = category === 'Preorder';
                                                return (
                                                    <Box key={category} display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems="center" gap={1}> 
                                                        <Typography color="secondary" sx={{ minWidth: '60px' }}>
                                                            {category}
                                                        </Typography>
                                                        <Box flexGrow={1} display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" gap={1}>
                                                            {[
                                                                { label: 'Cấp đi', type: 'StockIn' },
                                                                { label: 'Bàn giao tổ trước', type: 'PreviousReceipt' },
                                                                { label: 'Thu hồi đổi tổ', type: 'NextRetrieval' },
                                                                { label: 'Thu hồi thực tế', type: 'StockOn' },
                                                                { label: 'Hỏng hủy', type: 'Shrinkage' },
                                                                { label: 'Combo', type: 'Combo' },
                                                                { label: 'Thất thoát', type: 'Loss' },
                                                                { label: 'Tiêu thụ', type: 'Consume', disabled: true },
                                                            ].map(({ label, type, disabled }, index) => {
                                                                let value = 0;
                                                                if (isPreorder) {
                                                                    value = product[`preorder${type}`] || 0; // Map to Preorder fields
                                                                } else {
                                                                    value = product[`direct${type}`] || 0; // Map to Direct fields
                                                                }

                                                                return (
                                                                    <Box key={index} display="flex" justifyContent="space-between" alignItems="center" gap={3}>
                                                                        <Typography
                                                                            variant="body2"
                                                                            pl={1}
                                                                            sx={{
                                                                                fontWeight: 'medium',
                                                                                textAlign: isMobile ? 'left' : 'center',
                                                                            }}
                                                                        >
                                                                            {label}
                                                                        </Typography>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                fontWeight: 'medium',
                                                                                textAlign: isMobile ? 'left' : 'center',
                                                                                color: value !== 0 ? primaryDark : null,
                                                                            }}
                                                                        >
                                                                            {formatNumber(value)}
                                                                        </Typography>                                                    
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Box>                                
                                                    </Box>
                                                );                                    
                                            })}
                                        </Box>
                                    </Box>
                                ))}

                                <Box 
                                    display="flex" 
                                    flexDirection={isMobile ? 'column' : 'column'} 
                                    justifyContent="space-between" 
                                    gap={1} 
                                    py={1}
                                    borderBottom={`1px solid ${neutralDark}`}
                                >
                                    <Box display="flex" justifyContent={"flex-start"} gap={1}>
                                        <Typography color={primaryDark}>
                                            TOTAL
                                        </Typography> 
                                    </Box>
                                    <Box display="flex" flexDirection={isMobile ? 'row' : 'column'} justifyContent="space-between" alignItems="flex-start" gap={2}>                                
                                        {['Preorder', 'Direct'].map((category) => {
                                            const isPreorder = category === 'Preorder';
                                            const categoryTotals = isPreorder ? preorderTotals : directTotals;

                                            return (
                                                <Box key={category} display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems="center" gap={1}> 
                                                    <Typography color="secondary" sx={{ minWidth: '60px' }}>
                                                        {category} Total
                                                    </Typography>
                                                    <Box flexGrow={1} display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" gap={1}>
                                                        {[
                                                            { label: 'Cấp đi', type: 'stockIn' },
                                                            { label: 'Bàn giao tổ trước', type: 'previousReceipt' },
                                                            { label: 'Thu hồi đổi tổ', type: 'nextRetrieval' },
                                                            { label: 'Thu hồi thực tế', type: 'stockOn' },
                                                            { label: 'Hỏng hủy', type: 'shrinkage' },
                                                            { label: 'Combo', type: 'combo' },
                                                            { label: 'Thất thoát', type: 'loss' },
                                                            { label: 'Tiêu thụ', type: 'consume', disabled: true },
                                                        ].map(({ label, type, disabled }, index) => {

                                                            return (
                                                                <Box key={index} display="flex" justifyContent="space-between" alignItems="center" gap={3}>
                                                                    <Typography
                                                                        variant="body2"
                                                                        pl={1}
                                                                        sx={{
                                                                            fontWeight: 'medium',
                                                                            textAlign: isMobile ? 'left' : 'center',
                                                                        }}
                                                                    >
                                                                        {label}
                                                                    </Typography>
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{
                                                                            fontWeight: 'medium',
                                                                            textAlign: isMobile ? 'left' : 'center',
                                                                            color: categoryTotals[type] !== 0 ? primaryDark : null,
                                                                        }}
                                                                    >
                                                                        {formatNumber(categoryTotals[type])}
                                                                    </Typography>                                                    
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>                                
                                                </Box>
                                            );                                    
                                        })}
                                    </Box>
                                </Box>
                            </Box>                            
                        )}
                    </Box>
                ) : (
                    <Typography>No inventory data available.</Typography>
                )}               
            </DialogContent>
            <DialogActions>             
                {error && <Typography color="error">{error.message}</Typography>}  
                
                <Button 
                    variant="outlined" 
                    color="secondary" 
                    onClick={handleCloseDialog}
                >
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default InventoryByProductIdDialog;